import { NavLink, useLocation } from 'react-router';
import { Building2, Check, ChevronDown, KeyRound, LogOut } from 'lucide-react';
import { clsx } from 'clsx';
import { Avatar } from '@/components/Avatar';
import { Logo } from '@/components/Logo';
import { getNavItems, PASSWORD_PATH, PROFILE_PATH, type NavItem } from '@/security/navigation';
import { useAccessRoles } from '@/security/useAccessRoles';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCompanyStore } from '@/stores/useCompanyStore';
import { useFollowAbsencesToSign } from '@/stores/useAbsencesToSign';
import { useFollowPendingValidation } from '@/stores/usePendingValidation';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

/** Pastilles du menu : ce qui attend l'utilisateur (absences à signer, feuilles à valider) */
const BADGES: Record<string, (absencesToSign: number, pendingValidation: number) => number> = {
  '/requests': (absencesToSign) => absencesToSign,
  '/validation': (_, pendingValidation) => pendingValidation,
};

/** Libellé de la pastille pour les lecteurs d'écran */
const BADGE_LABELS: Record<string, (count: number) => string> = {
  '/requests': (count) => `${count} absence${count > 1 ? 's' : ''} à signer`,
  '/validation': (count) => `${count} feuille${count > 1 ? 's' : ''} à valider`,
};

function NavEntry({ item, onClick, badge = 0 }: { item: NavItem; onClick: () => void; badge?: number }) {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={({ isActive }) =>
        clsx(
          'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
          isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={clsx('w-5 h-5', isActive ? 'text-blue-600' : 'text-slate-400')} />
          {item.label}
          {badge > 0 && (
            <span
              className="ml-auto min-w-5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-xs font-semibold text-center"
              aria-label={BADGE_LABELS[item.path]?.(badge)}
              data-testid="nav-badge"
            >
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/** Bloc « Paramétrage » de la sidebar (Administrateurs, Dirigeant), masqué sans page accessible */
function SettingsSection({
  title,
  badgeClassName,
  items,
  onClick,
}: {
  title: string;
  badgeClassName: string;
  items: NavItem[];
  onClick: () => void;
}) {
  if (!items.length) return null;

  return (
    <section aria-label={`Paramétrage ${title.toLowerCase()}`}>
      <h3 className="px-4 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
        Paramétrage
        <span className={clsx('px-1.5 py-0.5 rounded text-[10px]', badgeClassName)}>{title}</span>
      </h3>
      <div className="space-y-1">
        {items.map((item) => (
          <NavEntry key={item.path} item={item} onClick={onClick} />
        ))}
      </div>
    </section>
  );
}

export function Sidebar({ isOpen, setIsOpen }: SidebarProps) {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const { pathname } = useLocation();

  // Les pages du Dirigeant suivent la société sélectionnée
  const items = getNavItems(useAccessRoles());
  const mainItems = items.filter((item) => item.section === 'main');
  const absencesToSign = useFollowAbsencesToSign();
  const pendingValidation = useFollowPendingValidation(pathname);
  const adminItems = items.filter((item) => item.section === 'adminSettings');
  const directorItems = items.filter((item) => item.section === 'directorSettings');
  const close = () => setIsOpen(false);

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={clsx(
          'fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={close}
      />

      <aside
        aria-label="Navigation principale"
        className={clsx(
          'fixed top-0 left-0 z-50 h-screen w-64 bg-card border-r border-slate-200 transition-transform duration-300 ease-in-out lg:translate-x-0 lg:sticky flex flex-col',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="p-6 flex items-center gap-3 flex-shrink-0">
          <Logo />
          <div>
            <p className="font-bold text-xl text-slate-900 leading-none">Teiji</p>
            <p className="text-xs text-slate-500 font-medium mt-1">HS Management</p>
          </div>
        </div>

        <nav className="flex-1 px-4 py-4 space-y-6 overflow-y-auto flex flex-col">
          <div className="space-y-1 flex-1">
            {mainItems.map((item) => (
              <NavEntry
                key={item.path}
                item={item}
                onClick={close}
                // Absences à signer (Mes Demandes), feuilles en attente du manager (Validation)
                badge={BADGES[item.path]?.(absencesToSign, pendingValidation) ?? 0}
              />
            ))}
          </div>

          {(adminItems.length > 0 || directorItems.length > 0) && (
            <div className="pt-4 mt-auto border-t border-slate-100 space-y-6">
              <SettingsSection
                title="DIRIGEANT"
                badgeClassName="bg-indigo-100 text-indigo-700"
                items={directorItems}
                onClick={close}
              />
              <SettingsSection
                title="ADMINISTRATEURS"
                badgeClassName="bg-red-100 text-red-600"
                items={adminItems}
                onClick={close}
              />
            </div>
          )}
        </nav>

        <div className="border-t border-slate-200 bg-slate-50/50 flex-shrink-0">
          {/* Société courante : les écrans par société (Employés…) s'y limitent */}
          <div className="p-4 border-b border-slate-200">
            <CompanySwitcher />
          </div>

          <div className="p-4">
            <div className="flex items-center gap-3 px-2">
              <Avatar
                firstName={user?.firstName ?? ''}
                lastName={user?.lastName ?? ''}
                tone="accent"
                className="font-semibold border border-slate-200"
              />
              <NavLink
                to={PROFILE_PATH}
                // Page de retour après enregistrement du profil
                state={pathname !== PROFILE_PATH ? { from: pathname } : undefined}
                onClick={close}
                title="Mon profil"
                className="flex-1 min-w-0 hover:opacity-80"
              >
                <p className="text-sm font-medium text-slate-900 truncate" data-testid="current-user">
                  {user?.lastName} {user?.firstName}
                </p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </NavLink>
              <NavLink
                to={PASSWORD_PATH}
                onClick={close}
                className="text-slate-400 hover:text-slate-600"
                title="Mon mot de passe"
              >
                <KeyRound className="w-5 h-5" />
              </NavLink>
              <button onClick={logout} className="text-slate-400 hover:text-slate-600" title="Se déconnecter">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

/** Sélecteur de société (prototype : bas de la sidebar, menu vers le haut) */
function CompanySwitcher() {
  const companies = useCompanyStore((state) => state.companies);
  const loaded = useCompanyStore((state) => state.loaded);
  const select = useCompanyStore((state) => state.select);
  // La société choisie s'affiche aussitôt, sans attendre le rattachement de l'utilisateur
  const current = useCompanyStore((state) => state.companies.find((company) => company.id === state.currentCompanyId));
  const canSwitch = companies.length > 1;

  const label = (
    <>
      <div className="w-8 h-8 rounded bg-indigo-100 flex items-center justify-center text-indigo-700 flex-shrink-0">
        <Building2 className="w-4 h-4" />
      </div>
      <div className="flex-1 text-left min-w-0">
        <p className="text-xs text-slate-500 font-medium">Société</p>
        <p className="text-sm font-bold text-slate-900 truncate" data-testid="current-company">
          {current?.name ?? (loaded ? 'Aucune société' : 'Chargement…')}
        </p>
      </div>
    </>
  );

  if (!canSwitch) {
    return (
      <div className="w-full flex items-center gap-3 px-3 py-2.5 bg-card border border-slate-200 rounded-lg">
        {label}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Changer de société"
        className="w-full flex items-center gap-3 px-3 py-2.5 bg-card border border-slate-200 rounded-lg hover:border-blue-400 hover:shadow-sm transition-all"
      >
        {label}
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-(--radix-dropdown-menu-trigger-width) bg-card">
        {companies.map((company) => (
          <DropdownMenuItem
            key={company.id}
            onSelect={() => select(company.id)}
            className={clsx('px-4 py-3', company.id === current?.id && 'text-blue-700 bg-blue-50 font-medium')}
          >
            <span className="flex-1">{company.name}</span>
            {company.id === current?.id && <Check className="w-4 h-4 text-blue-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
